/**
 * 应用版本管理页面
 * 用于管理Android/iOS应用的在线升级版本
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Edit,
  Trash2,
  Download,
  RefreshCw,
  Eye,
  Power,
  PowerOff,
} from 'lucide-react';
import { toast } from 'sonner';
import type { AppVersion, VersionListQuery } from '@xiaozhi/shared-types';
import { UpdateType, PlatformType, VersionStatus } from '@xiaozhi/shared-types';
import { getVersionList, deleteVersion, updateVersion, getApkDownloadUrl } from '@/api/appVersion';
import dayjs from 'dayjs';

export function AppVersionManagementPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState<AppVersion[]>([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState<VersionListQuery>({
    platform: PlatformType.ANDROID,
    page: 1,
    pageSize: 20,
  });

  // 加载版本列表
  const loadVersions = async () => {
    setLoading(true);
    try {
      const result = await getVersionList(query);
      setVersions(result.items);
      setTotal(result.total);
    } catch (error: any) {
      toast.error(error.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVersions();
  }, [query]);

  // 删除版本
  const handleDelete = async (id: number) => {
    try {
      await deleteVersion(id);
      toast.success('删除成功');
      loadVersions();
    } catch (error: any) {
      toast.error(error.message || '删除失败');
    }
  };

  // 切换版本状态
  const handleToggleStatus = async (record: AppVersion) => {
    try {
      const newStatus =
        record.status === VersionStatus.PUBLISHED ? VersionStatus.DISABLED : VersionStatus.PUBLISHED;
      
      const formData = new FormData();
      formData.append('status', newStatus.toString());
      
      await updateVersion(record.id, formData);
      toast.success(newStatus === VersionStatus.PUBLISHED ? '已启用' : '已禁用');
      loadVersions();
    } catch (error: any) {
      toast.error(error.message || '操作失败');
    }
  };

  // 下载APK
  const handleDownload = (record: AppVersion) => {
    const filename = record.download_url.split('/').pop();
    if (filename) {
      const url = getApkDownloadUrl(filename);
      window.open(url, '_blank');
    }
  };

  // 渲染更新类型标签
  const renderUpdateTypeBadge = (type: UpdateType) => {
    const variants = {
      [UpdateType.OPTIONAL]: { variant: 'default' as const, label: '可选更新' },
      [UpdateType.FORCE]: { variant: 'destructive' as const, label: '强制更新' },
      [UpdateType.SILENT]: { variant: 'secondary' as const, label: '静默更新' },
    };
    const config = variants[type];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <LayoutWithSidebar title="应用版本管理">
      <div className="flex-1 overflow-auto">
          <div className="container mx-auto py-8 px-4 max-w-7xl">
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">应用版本管理</h1>
              <p className="text-gray-600 dark:text-gray-400">
                管理Android/iOS应用的在线升级版本
              </p>
            </div>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>版本列表</CardTitle>
                    <CardDescription>共 {total} 个版本</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Select
                      value={query.platform}
                      onValueChange={(value) =>
                        setQuery({ ...query, platform: value as PlatformType, page: 1 })
                      }
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={PlatformType.ANDROID}>Android</SelectItem>
                        <SelectItem value={PlatformType.IOS}>iOS</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select
                      value={query.status?.toString() || 'all'}
                      onValueChange={(value) =>
                        setQuery({
                          ...query,
                          status: value === 'all' ? undefined : Number.parseInt(value),
                          page: 1,
                        })
                      }
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="状态" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">全部状态</SelectItem>
                        <SelectItem value={VersionStatus.DRAFT.toString()}>草稿</SelectItem>
                        <SelectItem value={VersionStatus.PUBLISHED.toString()}>已发布</SelectItem>
                        <SelectItem value={VersionStatus.DISABLED.toString()}>已禁用</SelectItem>
                      </SelectContent>
                    </Select>

                    <Button variant="outline" size="icon" onClick={loadVersions} disabled={loading}>
                      <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>

                    <Button onClick={() => navigate('/app-version/create')}>
                      <Plus className="h-4 w-4 mr-2" />
                      发布新版本
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>版本号</TableHead>
                        <TableHead>版本代码</TableHead>
                        <TableHead>更新类型</TableHead>
                        <TableHead>平台</TableHead>
                        <TableHead>渠道</TableHead>
                        <TableHead>文件大小</TableHead>
                        <TableHead>灰度</TableHead>
                        <TableHead>状态</TableHead>
                        <TableHead>创建时间</TableHead>
                        <TableHead className="text-right">操作</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-8">
                            加载中...
                          </TableCell>
                        </TableRow>
                      ) : versions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-8">
                            暂无数据
                          </TableCell>
                        </TableRow>
                      ) : (
                        versions.map((version) => (
                          <TableRow key={version.id}>
                            <TableCell className="font-medium">{version.version_name}</TableCell>
                            <TableCell>{version.version_code}</TableCell>
                            <TableCell>{renderUpdateTypeBadge(version.update_type)}</TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {version.platform === PlatformType.ANDROID ? 'Android' : 'iOS'}
                              </Badge>
                            </TableCell>
                            <TableCell>{version.channel}</TableCell>
                            <TableCell>{(version.file_size / 1024 / 1024).toFixed(2)} MB</TableCell>
                            <TableCell>
                              {version.gray_enabled ? (
                                <Badge variant="secondary">{version.gray_percentage}%</Badge>
                              ) : (
                                <span className="text-gray-400">未启用</span>
                              )}
                            </TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    version.status === VersionStatus.PUBLISHED
                                      ? 'default'
                                      : version.status === VersionStatus.DRAFT
                                      ? 'secondary'
                                      : 'outline'
                                  }
                                >
                                  {version.status === VersionStatus.PUBLISHED
                                    ? '已发布'
                                    : version.status === VersionStatus.DRAFT
                                    ? '草稿'
                                    : '已禁用'}
                                </Badge>
                              </TableCell>
                            <TableCell>
                              {dayjs(version.create_time).format('YYYY-MM-DD HH:mm')}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => navigate(`/app-version/detail/${version.id}`)}
                                  title="查看详情"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => navigate(`/app-version/edit/${version.id}`)}
                                  title="编辑"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDownload(version)}
                                  title="下载"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleToggleStatus(version)}
                                  title={
                                    version.status === VersionStatus.PUBLISHED ? '禁用版本' : '启用版本'
                                  }
                                >
                                  {version.status === VersionStatus.PUBLISHED ? (
                                    <PowerOff className="h-4 w-4" />
                                  ) : (
                                    <Power className="h-4 w-4" />
                                  )}
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="icon" title="删除">
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>确定要删除这个版本吗？</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        删除后无法恢复，APK文件也会被删除。
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>取消</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDelete(version.id)}>
                                        确定删除
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* 分页 */}
                {total > query.pageSize! && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-gray-600">
                      显示 {(query.page! - 1) * query.pageSize! + 1} 到{' '}
                      {Math.min(query.page! * query.pageSize!, total)} 条，共 {total} 条
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={query.page === 1}
                        onClick={() => setQuery({ ...query, page: query.page! - 1 })}
                      >
                        上一页
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={query.page! * query.pageSize! >= total}
                        onClick={() => setQuery({ ...query, page: query.page! + 1 })}
                      >
                        下一页
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
      </div>
    </LayoutWithSidebar>
  );
}

